/**
 * NotesTab — Notes CRUD table for the Event Profile.
 *
 * Features: add note, edit note, delete notes, CSV export.
 *
 * Props:
 *   notes           — array of note records
 *   editRights      — boolean (can add/edit/delete)
 *   onAddNote       — () => void
 *   onEditNote      — (note) => void
 *   onDeleteNotes   — (noteIds) => void
 *   tzOffset        — timezone offset for datetime display
 *   eventId         — event ID (for CSV filename)
 */

import { useState, useMemo, useCallback } from 'react';
import { CSVLink } from 'react-csv';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';
import RowActionsMenu from '../../../../components/v2/RowActionsMenu.jsx';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';

const CSV_HEADERS = [
    { label: 'Timestamp', key: 'last_modified' },
    { label: 'Created By', key: 'display_name' },
    { label: 'Note', key: 'note' },
    { label: 'Created On', key: 'create_time' },
];

const NoteRowActions = ({ note, onEdit, onDelete, editRights }) => {
    if (!editRights) return null;

    return (
        <RowActionsMenu label="Actions" classPrefix="ap">
            {(close) => (
                <>
                    <button type="button" className="ap-row-actions__item" onClick={() => { close(); onEdit(note); }}>
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                        </svg>
                        Edit Note
                    </button>
                    <button type="button" className="ap-row-actions__item ap-row-actions__item--danger" onClick={() => { close(); onDelete([note.note_id]); }}>
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                        Delete Note
                    </button>
                </>
            )}
        </RowActionsMenu>
    );
};

const NotesTab = ({ notes = [], editRights, onAddNote, onEditNote, onDeleteNotes, tzOffset, eventId }) => {
    const [sortColumn, setSortColumn] = useState('create_time');
    const [sortOrder, setSortOrder] = useState('desc');

    const handleSort = useCallback((key) => {
        if (sortColumn === key) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(key);
            setSortOrder('asc');
        }
    }, [sortColumn]);

    // Sort
    const sorted = useMemo(() => {
        const arr = [...notes];
        arr.sort((a, b) => {
            const aVal = a[sortColumn] || '';
            const bVal = b[sortColumn] || '';
            const cmp = String(aVal).localeCompare(String(bVal));
            return sortOrder === 'asc' ? cmp : -cmp;
        });
        return arr;
    }, [notes, sortColumn, sortOrder]);

    // CSV filename
    const csvFilename = useMemo(() => {
        const idPart = eventId ? String(eventId).split('@')[1] || eventId : 'event';
        return `${idPart}_notes.csv`;
    }, [eventId]);

    return (
        <div>
            {/* Controls */}
            <div className="ep-notes-controls">
                <div className="ep-notes-controls__left">
                    {editRights ? (
                        <button type="button" className="dp-btn dp-btn--primary dp-btn--sm" onClick={onAddNote}>
                            <svg style={{ width: '14px', height: '14px' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                            Add Note
                        </button>
                    ) : null}
                </div>
                <div className="ep-notes-controls__right">
                    {sorted.length > 0 ? (
                        <CSVLink
                            data={sorted}
                            headers={CSV_HEADERS}
                            filename={csvFilename}
                            className="rp-btn rp-btn--secondary"
                            style={{ textDecoration: 'none' }}
                        >
                            <i className="fe fe-download" style={{ marginRight: '4px' }} />CSV
                        </CSVLink>
                    ) : null}
                </div>
            </div>

            {/* Table */}
            <div className="dp-table-wrap">
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            <th className="ap-th" aria-sort={ariaSort(sortColumn, sortOrder, 'create_time')}>
                                <div className="ap-th__content">
                                    <button type="button" onClick={() => handleSort('create_time')} className="ap-th__sort-btn">
                                        Timestamp
                                        {sortColumn === 'create_time' ? (
                                            <svg className="ap-icon-sm" style={{ marginLeft: '2px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                                {sortOrder === 'asc'
                                                    ? <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                                    : <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                                }
                                            </svg>
                                        ) : null}
                                    </button>
                                </div>
                            </th>
                            <th className="ap-th"><div className="ap-th__content"><span>Created By</span></div></th>
                            <th className="ap-th"><div className="ap-th__content"><span>Note</span></div></th>
                            {editRights ? (
                                <th className="ap-th ap-th--actions">Edit</th>
                            ) : null}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.length === 0 ? (
                            <tr className="dp-tr">
                                <td className="dp-td dp-td--center" colSpan={editRights ? 4 : 3} style={{ padding: 'var(--space-8)' }}>
                                    <span className="dp-cell-text dp-cell-text--muted">No notes for this event.</span>
                                </td>
                            </tr>
                        ) : null}
                        {sorted.map((note, idx) => (
                            <tr key={note.note_id || idx} className="dp-tr">
                                <td className="dp-td">
                                    <span className="dp-cell-text" style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 'var(--font-size-xs)' }}>
                                        {note.create_time ? convertIsoUTCToLocal(note.create_time, tzOffset) : '\u2014'}
                                    </span>
                                </td>
                                <td className="dp-td">
                                    <span className="dp-cell-text">{note.display_name || '\u2014'}</span>
                                </td>
                                <td className="dp-td">
                                    <span className="dp-cell-text">{note.note || '\u2014'}</span>
                                </td>
                                {editRights ? (
                                    <td className="dp-td dp-td--center">
                                        <NoteRowActions
                                            note={note}
                                            onEdit={onEditNote}
                                            onDelete={onDeleteNotes}
                                            editRights={editRights}
                                        />
                                    </td>
                                ) : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default NotesTab;
